import { PurchaseCard } from '@components'

import { Text } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { useSubscriptionPlanPrices } from '../../../../hooks/useSubscriptionPlanPrices'
import { resolveSubscriptionPlan } from '../../../../utils/resolveSubscriptionPlan'
import { useSelectPlan } from '../../contexts/planSelectionContext'
import { useIsCurrentPlan } from '../../hooks/useIsCurrentPlan'
import { getPlanPriceSuffixTKey } from '../../utils/getPlanPriceSuffixTKey'

interface CompletePlanCardProps {
  plan: SubscriptionPlan
}

export const CompletePlanCard = ({ plan }: CompletePlanCardProps): ReactElement => {
  const { t } = useTranslation()
  const { selectPlan } = useSelectPlan()
  const { isCurrentPlan } = useIsCurrentPlan(plan)
  const { monthlyPrice, isLoading: isLoadingPrice } = useSubscriptionPlanPrices(plan)

  const { content: planContent } = useMemo(() => resolveSubscriptionPlan(plan), [plan])
  const {
    comparison = '',
    cta = '',
    description = '',
    features = [],
    featuresHeader = '',
    highlight = '',
    name,
  } = planContent

  const handleUpgradePlanButtonClick = useCallback(() => {
    selectPlan(plan, SubscriptionPeriod.Monthly)
  }, [selectPlan, plan])

  return (
    <PurchaseCard highlightedText={t(highlight)}>
      <PurchaseCard.Header description={t(description)} title={t(name)} />
      {isCurrentPlan ? (
        <PurchaseCard.Button disabled>{t('subscription.plan.current')}</PurchaseCard.Button>
      ) : (
        <PurchaseCard.Button onClick={handleUpgradePlanButtonClick}>{t(cta)}</PurchaseCard.Button>
      )}
      <PurchaseCard.PriceSection
        isLoading={isLoadingPrice}
        price={monthlyPrice}
        priceSuffix={t(getPlanPriceSuffixTKey('')) + '*'}
        pricePeriodNode={
          <Text variant="bodySM" colorVariant="secondary">
            {t('organization_subscription.monthly')}
          </Text>
        }
      />
      <PurchaseCard.Features
        footer={t('organization_subscription.plans.price_reference')}
        header={t(featuresHeader)}
        items={features.map((tKey) => ({ tKey }))}
        itemsLabel={t(comparison)}
      />
    </PurchaseCard>
  )
}
